export interface AppEnvironment {
  production: boolean;
  name: string;
  default: string;
  unlayer_project_key: number;
  dws_base_url: string;
  widget_base_url: string;
  features?: {
    [name: string]: boolean;
  };
  hosts: {
    [name: string]: AppHostConfig;
  };
  values: any;
}

export interface AppHostConfig {
  host?: string;
  protocol?: string;
  port?: string;
  root?: string;
  unauthenticated?: boolean;
  local?: boolean;
  endpoints: {
    [name: string]: string;
  };
}

export const environment: AppEnvironment = {
  production: false,
  name: "common",
  default: "v2",
  unlayer_project_key: 267816,
  dws_base_url: "https://dws-staging.divinea.com",
  widget_base_url: "https://divinea-widget.web.app",
  features: {
    "campaign-test-email": true,
    "reservation-boundless-datetime": true,
    automations: true,
    "sendgrid-statistics": true,
    "campaign-active": false,
    "company-management-privacy-files": true,
    "unsubscribe-campaign": true,
    "save-template": true,
    "company-management-payouts": true,
    "days-no-purchase-automation": true,
    "conversion-statistic": true,
    "subscribe-link": true,
    "subscription-conditions": false,
    "subscription-invitation-automation": true,
    "order-send-receipt-email": true,
    "external-id-channel": true,
    "product-channel-categories": true,
    "winery-channel-name": true,
    "archive-contact": true,
    "instant-booking": true,
    "delete-channels": false,
    "winery-transactional-emails": true,
    "paypal-split-payment": false,
    "google-calendar-integration": false,
    winelivery: true,
    "automatic-approve": true,
    wineplatform: true,
    spotty: true,
    "filters-widget": true,
    "country-in-reservation": true,
    "category-product": true,
    "availability-data-pdf": true,
    "close-matching-slot-languages-in-the-same-experience": true,
    "automatically-accept-reservations-on-same-slot": true,
    "dwspay-payment-methods-enabled": true,
    "ai-agent": true,
    "ecommerce": true
  },
  values: {
    "storage-key": "winery-saas",
    "fcm-worker": "assets/js/firebase-messaging-sw-staging.js",
    "manual-url": "https://saas-manual.s3-eu-west-1.amazonaws.com/index.html",
    cache: {
      enabled: false,
      "max-age": 10 * 60 * 1000,
    },
    firebaseConfig: {},
    dateSendGridStatistics: "2021-03-30",
    "google-client-id": "209822336933-0kalbgqjn56g0aol2jnss4v86h9r86jg.apps.googleusercontent.com",
    "outlook-client-id": "259abbff-6218-4b25-9434-b1fcb4add280",
    "outlook-redirect-uri": "http://localhost:4200",
    /**
     * MSAL only: full redirect URI registered under Entra ID → App → Authentication → Single-page application.
     * Leave empty to use `{currentOrigin}/msal-redirect.html` (recommended for local dev on any port).
     * If set, value must match Azure exactly (scheme, host, port, path, no trailing slash on path).
     */
    "msal-redirect-uri-override": "",
    "preorder-url": "https://preorders-staging.divinea.com",
    "stripe-dashboard-url": "https://dashboard.stripe.com/test"
  },
  hosts: {
    crm: {
      root: "/api/",
      endpoints: {
        'shipping/wineryId/shipping-settings': 'shipping/{wineryId}/shipping-settings',
        'shipping/wineryId/shipping-settings/offers': 'shipping/{wineryId}/shipping-settings/offers',
        'shipping/wineryId/simulate': 'shipping/{wineryId}/simulate',
        'shipping/wineryId/areas': 'shipping/{wineryId}/areas',
        'shipping/wineryId/zones': 'shipping/{wineryId}/zones',
        'shipping/wineryId/zones/preview': 'shipping/{wineryId}/zones/preview',
        'shipping/wineryId/zones/zoneId': 'shipping/{wineryId}/zones/{zoneId}',
        'shipping/wineryId/archive-blockers': 'shipping/{wineryId}/archive-blockers',
        'shipping/wineryId/follow-ups': 'shipping/{wineryId}/follow-ups',
        'shipping/wineryId/follow-ups/shippingDataId/track': 'shipping/{wineryId}/follow-ups/{shippingDataId}/track',
        'shipping/wineryId/follow-ups/shippingDataId/mark-paid': 'shipping/{wineryId}/follow-ups/{shippingDataId}/mark-paid',
        'shipping/wineryId/follow-ups/shippingDataId/cancel': 'shipping/{wineryId}/follow-ups/{shippingDataId}/cancel',
        'shipping/wineryId/follow-ups/shippingDataId/suggest-parcels': 'shipping/{wineryId}/follow-ups/{shippingDataId}/suggest-parcels',
        'ecommerce/shops': 'ecommerce/shops',
        'ecommerce/shops/id': 'ecommerce/shops/{id}',
        'ecommerce/shops/id/duplicate': 'ecommerce/shops/{id}/duplicate',
        'availability-block': 'availability-block',
        'availability-block/id': 'availability-block/{id}',
        'person-contacts': 'person-contacts',
        'person-contacts/id': 'person-contacts/{id}',
        'company-contacts': 'company-contacts',
        'company-contacts/id': 'company-contacts/{id}',
        'company-person-contacts': 'company-person-contacts',
        'company-person-contacts/companyContactId/personContactId': 'company-person-contacts/{companyContactId}/{personContactId}',
        'contacts-wineries': 'contacts-wineries',
        'contacts-wineries/contactId/wineryId': 'contacts-wineries/{contactId}/{wineryId}',
        'contacts-wineries/contactId/wineryId/unsubscribe': 'contacts-wineries/{contactId}/{wineryId}/unsubscribe',
        'contacts-wineries/contactId/wineryId/campaignId/unsubscribe': 'contacts-wineries/{contactId}/{wineryId}/{campaignId}/unsubscribe',
        'contacts-wineries/contactId/wineryId/archive': 'contacts-wineries/{contactId}/{wineryId}/archive',
        'contacts-wineries/contactId/wineryId/unarchive': 'contacts-wineries/{contactId}/{wineryId}/unarchive',
        'emails': 'emails',
        'emails/id': 'emails/{id}',
        'reservations': 'reservations',
        'sale-reservations/page': 'sale-reservations/page',
        'reservations/id': 'reservations/{id}',
        'ai-agent/sessions': 'ai-agent/sessions',
        'ai-agent/sessions/id': 'ai-agent/sessions/{id}',
        'ai-agent/sessions/id/messages': 'ai-agent/sessions/{id}/messages',
        'emails/email': 'emails/{email}',
        'emails/emailWithWinery': 'emails/emailWithWinery',
        'email/download/all/wineryId': 'email/download/all/{wineryId}',
        'email/download/not-delivered/wineryId': 'email/download/not-delivered/{wineryId}',
        'email/download/opened/wineryId': 'email/download/opened/{wineryId}',
        'email/download/not-opened/wineryId': 'email/download/not-opened/{wineryId}',
        'phones': 'phones',
        'phones/id': 'phones/{id}',
        'phones/phone': 'phones/phone/{phone}',
        'languages': 'languages',
        'contacts-languages': 'contacts-languages',
        'contacts-languages/contactId/languageId': 'contacts-languages/{contactId}/{languageId}',
        'contacts': 'contacts',
        'contacts/filter-by': 'contacts/filter-by',
        'contacts/matchers': 'contacts/matchers',
        'contacts/id': 'contacts/{id}',
        'contacts/v2/id': 'contacts/v2/{id}',
        'contacts/id/companyId': 'contacts/{id}/{companyId}',
        'contacts/email': 'v2/contacts/email',
        'contacts/update-short/id': 'v2/contacts/update-short/{id}',
        'v2/contacts': 'v2/contacts',
        'contacts/v2/id/companyId': 'contacts/v2/{id}/{companyId}',
        'contacts/ref': 'contacts/ref/{ref}',
        'contacts-tags': 'contacts-tags',
        'contacts-tags/contactId/tagId': 'contacts-tags/{contactId}/{tagId}',
        'contacts-origin/wineryId': 'contacts-origin/{wineryId}',
        'events': 'events',
        'events/id': 'events/{id}',
        'visit-frequencies': 'visit-frequencies',
        'origins': 'origins',
        'wine-preferences': 'wine-preferences',
        'customer-profiles': 'customer-profiles',
        'customer-classes': 'customer-classes',
        'addresses': 'addresses',
        'addresses/id': 'addresses/{id}',
        'addresses/cities': 'addresses/cities',
        'addresses/provinces': 'addresses/provinces',
        'countries': 'countries',
        'countries/id': 'countries/{id}',
        'reservations/reservationId': 'reservations/{reservationId}',
        'reservations/statistics': 'reservations/statistics',
        'reservations/reservationId/attach-gift': 'reservations/{reservationId}/attach-gift',
        'tags': 'tags',
        'tags/id': 'tags/{id}',
        'segments': 'segments',
        'segments/id': 'segments/{id}',
        'tags/winery/wineryId': 'tags/winery/{wineryId}',
        'contact-matchers': 'contact-matchers',
        'orders': 'orders',
        'orders/id': 'orders/{id}',
        'orders/v4/id': 'v4/orders/{id}',
        'orders/v4/id/order-details': 'v4/orders/{id}/order-details',
        'orders/v4/id/notes': 'v4/orders/{id}/notes',
        'orders/v4/id/logs': 'v4/orders/{id}/logs',
        'pos/v4/activity-log': 'v4/pos/activity-log',
        'orders/by-codes' : 'orders/by-codes',
        "orders/by-code": "orders/by-code",
        'orders/export': 'orders/export',
        'orders/orders-countries': 'orders/orders-countries',
        'orders/orders-statuses': 'orders/orders-statuses',
        "orders/order-shipping-data": "orders/order-shipping-data",
        "orders/orders-payment-types": "orders/orders-payment-types",
        'orders/id/send-receipt-email': 'orders/{id}/send-receipt-email',
        'orders/id/status-value': 'orders/{id}/status/{status}',
        'pos-orders/id': 'pos-orders/{id}',
        'statistics': 'statistics',
        'statistics/payments': 'statistics/payments',
        'statistics/orders': 'statistics/orders',
        'statistics/orders/products': 'statistics/orders/products',
        'statistics/campaigns': 'statistics/campaigns',
        'statistics/campaigns/multiple': 'statistics/campaigns/multiple',
        'statistics/campaigns/id': 'statistics/campaigns/{id}',
        'statistics/campaigns/id/summary': 'statistics/campaigns/{id}/summary',
        'statistics/campaigns/id/summarybydate': 'statistics/campaigns/{id}/summarybydate',
        'v2/statistics/campaigns/id': 'v2/statistics/campaigns/{id}',
        'product-types': 'product-types',
        'products': 'products',
        'products/checkname': 'products/checkname',
        'products/id': 'products/{id}',
        'products/id/active-toggle': 'products/{id}/active-toggle',
        'products/grid/wineryId': 'products/grid/{wineryId}',
        'products/id/sort-product': 'products/{id}/sort-product',
        'products/id/sort-product-by-category': 'products/{id}/sort-product-by-category',
        'products/productId/storage': 'products/{productId}/storage',
        'products/productId/storage/action': 'products/{productId}/storage/{action}',
        'products/storage/all': 'products/storage/all',
        'files': 'files',
        'files/id': 'files/{id}',
        'product-files': 'product-files',
        'products/id/orders': 'products/{id}/orders',
        'product-channels': 'product-channels',
        'product-channels/id': 'product-channels/{id}',
        'statistics/products': 'statistics/products',
        'v2/product-catalog/products': 'v2/product-catalog/products',
        'v2/product-catalog/products/id': 'v2/product-catalog/products/{id}',
        'v2/product-catalog/products/query': 'v2/product-catalog/products/query',
        'v2/product-catalog/products/id/publish': 'v2/product-catalog/products/{id}/publish',
        'v2/product-catalog/products/id/archive': 'v2/product-catalog/products/{id}/archive',
        'v2/product-catalog/products/id/draft': 'v2/product-catalog/products/{id}/draft',
        'v2/product-catalog/products/id/catalog-display-order': 'v2/product-catalog/products/{id}/catalog-display-order',
        'v2/product-catalog/products/id/inventory': 'v2/product-catalog/products/{id}/inventory',
        'v2/product-catalog/products/id/inventory-movements': 'v2/product-catalog/products/{id}/inventory-movements',
        'v2/product-catalog/products/id/sales-history': 'v2/product-catalog/products/{id}/sales-history',
        'v2/product-catalog/products/id/files': 'v2/product-catalog/products/{id}/files',
        'v2/product-catalog/products/id/files/upload': 'v2/product-catalog/products/{id}/files/upload',
        'v2/product-catalog/products/id/files/fileId': 'v2/product-catalog/products/{id}/files/{fileId}',
        'v2/product-catalog/products/id/files/reorder': 'v2/product-catalog/products/{id}/files/reorder',
        'v2/product-catalog/products/id/files/commit-temp': 'v2/product-catalog/products/{id}/files/commit-temp',
        'v2/product-catalog/categories': 'v2/product-catalog/categories',
        'v2/product-catalog/categories/query': 'v2/product-catalog/categories/query',
        'v2/product-catalog/categories/id': 'v2/product-catalog/categories/{id}',
        'v2/product-catalog/product-types': 'v2/product-catalog/product-types',
        'v2/product-catalog/attributes': 'v2/product-catalog/attributes',
        'v2/product-catalog/attributes/query': 'v2/product-catalog/attributes/query',
        'v2/product-catalog/attributes/id': 'v2/product-catalog/attributes/{id}',
        'v2/product-catalog/attributes/id/values': 'v2/product-catalog/attributes/{id}/values',
        'v2/product-catalog/attributes/id/values/valueId': 'v2/product-catalog/attributes/{id}/values/{valueId}',
        'v2/product-catalog/attributes/id/values/valueId/check-delete': 'v2/product-catalog/attributes/{id}/values/{valueId}/check-delete',
        'v2/receipts/id': 'v2/receipts/{id}',
        'v2/receipts/query': 'v2/receipts/query',
        'winery/wineryId/category': 'winery/{wineryId}/category',
        'winery/wineryId/closing-days': 'winery/{wineryId}/closing-days',
        'winery/wineryId/category/id': 'winery/{wineryId}/category/{id}',
        'winery/wineryId/category/id/priority': 'winery/{wineryId}/category/{id}/priority',
        'winery/wineryId/wineryChannel/id/pos-terminal': 'winery/{wineryId}/wineryChannel/{id}/pos-terminal',
        'winery-channels': 'winery-channels',
        'winery-channels/id': 'winery-channels/{id}',
        'winery-channels/id/categories': 'winery-channels/{id}/categories',
        'winery-channels/id/products': 'winery-channels/{id}/products',
        'point-of-sale-wine-suite': 'point-of-sale-wine-suite',
        'point-of-sale-wine-suite/id': 'point-of-sale-wine-suite/{id}',
        'point-of-sale-wine-suite/id/unlock': 'point-of-sale-wine-suite/{id}/unlock',
        'fiscal-nature': 'fiscal-nature',
        'order-payment-types': 'order-payment-types',
        'order-status': 'order-status',
        'coupons': 'coupons',
        'coupons/id': 'coupons/{id}',
        'audiences': 'audiences',
        'audiences/id': 'audiences/{id}',
        'audiences/{audienceId}/contacts': 'audiences/{audienceId}/contacts',
        'audiences/winery/wineryId': 'audiences/winery/{wineryId}',
        'audience-contacts': 'audience-contacts',
        'campaigns': 'campaigns',
        'campaigns/id': 'campaigns/{campaignId}',
        'campaigns/id/clone': 'campaigns/{campaignId}/clone',
        'campaigns/id/clone-with-contacts': 'campaigns/{campaignId}/clone-with-contacts',
        'campaigns/id/activate': 'campaigns/{campaignId}/activate',
        'campaigns/id/deactivate': 'campaigns/{campaignId}/deactivate',
        'campaigns/test-email': 'campaigns/test-email',
        'campaign-templates': 'campaign-templates',
        'campaign-templates/id': 'campaign-templates/{campaignTemplateId}',
        'campaign-targets': 'campaign-targets',
        'campaign-targets/id': 'campaign-targets/{campaignTargetId}',
        'campaign-targets/campaign/campaignId': 'campaign-targets/campaign/{campaignId}',
        'campaign-origins': 'campaign-origins',
        'campaign-origins/campaign/campaignId': 'campaign-origins/campaign/{campaignId}',
        'campaign-contacts': 'campaign-contacts',
        'campaign-contacts/for-selection': 'campaign-contacts/for-selection',
        'campaign-contacts/id': 'campaign-contacts/{campaignId}',
        'campaign-contacts/campaign/campaignId': 'campaign-contacts/campaign/{campaignId}',
        'campaign-target-wineries/campaignId': 'campaign-target-wineries/{campaignId}',
        'campaigns/id/contacts': 'campaigns/{campaignId}/contacts',
        'contact-segments': 'contact-segments',
        'contact-segments/contactId/segmentId': 'contact-segments/{contactId}/{segmentId}',
        'automation-types': 'automation-types',
        'automation-types/id': 'automation-types/{id}',
        'track': 'track/{code}',
        'wineries': 'wineries',
        'admin/jobs/contact': 'admin/jobs/contact',
        'admin/jobs/contact/import': 'admin/jobs/contact/import',
        'admin/transactional-emails': 'admin/transactional-emails',
        'admin/transactional-emails/type': 'admin/transactional-emails/{type}',
        'admin/transactional-emails/draft/type': 'admin/transactional-emails/draft/{type}',
        'admin/transactional-emails/clone/type': 'admin/transactional-emails/clone/{type}',
        'admin/transactional-emails/id': 'admin/transactional-emails/{id}',
        'admin/transactional-emails/draft/id': 'admin/transactional-emails/draft/{id}',
        'admin/transactional-emails/id/clone': 'admin/transactional-emails/clone/{id}',
        'admin/transactional-emails/restore/id': 'admin/transactional-emails/restore/{id}',
        'admin/transactional-emails/send-test': 'admin/transactional-emails/send-test',
        'admin/ws-parameters': 'admin/ws-parameters',
        'admin/notifications': 'admin/notifications',
        'admin/notifications/campaignId': 'admin/notifications/{campaignId}',
        'admin/notifications/campaignId/publication-paused': 'admin/notifications/{campaignId}/publication-paused',
        'admin/notifications/campaignId/stats': 'admin/notifications/{campaignId}/stats',
        'admin/notifications/push': 'admin/notifications/push',
        'login': 'login',
        'transactional-emails': 'transactional-emails',
        'transactional-emails/type': 'transactional-emails/{type}',
        'transactional-emails/draft/type': 'transactional-emails/draft/{type}',
        'transactional-emails/clone/type': 'transactional-emails/clone/{type}',
        'transactional-emails/id': 'transactional-emails/{id}',
        'transactional-emails/draft/id': 'transactional-emails/draft/{id}',
        "transactional-emails/new-draft/type": "transactional-emails/new-draft/{type}",
        'transactional-emails/clone/id': 'transactional-emails/clone/{id}',
        'transactional-emails/delete/id': 'transactional-emails/delete/{id}',
        'transactional-emails/restore/id': 'transactional-emails/restore/{id}',
        'transactional-emails/send-test': 'transactional-emails/send-test',
        'transactional-email-experiences': 'transactional-email-experiences',
        'template-html': 'template-html',
        'admin/template-html':'admin/template-html',
        'admin/template-html/type':'admin/template-html/{type}',
        'admin/template-html/type/draft':'admin/template-html/{type}/draft',
        'admin/template-html/get-pdf':'admin/template-html/get-pdf',
        'template-html/type': 'template-html/{type}',
        'template-html/type/draft': 'template-html/{type}/draft',
        'template-html/get-pdf': 'template-html/get-pdf',
        'inactive-transactional-email/wineryId': 'inactive-transactional-email/{wineryId}',
        'inactive-transactional-email/wineryId/id': 'inactive-transactional-email/{wineryId}/{id}',
        'contact-privacy-history/contactId/wineryId': 'contact-privacy-history/{contactId}/{wineryId}',
        'channels': 'channels',
        'appointments': 'appointments',
        'appointments/id': 'appointments/{id}',
        'google-auth-tokens': 'google-auth-tokens',
        'google-auth-tokens/id': 'google-auth-tokens/{id}',
        'outlook-auth-tokens': 'outlook-auth-tokens',
        'outlook-auth-tokens/id': 'outlook-auth-tokens/{id}',
        'winery-scheduling': 'winery-scheduling',
        'winery-scheduling/experiences-title': 'winery-scheduling/experiences-title',
        'winery-scheduling/origins': 'winery-scheduling/origins',
        'scheduling/page': 'scheduling/page',
        'scheduling-old/date-range': 'scheduling-old/date-range',
        'scheduling/date-range': 'scheduling/date-range',
        'scheduling/check-requests-ids': 'scheduling/check-requests-ids',
        'scheduling/advanced-filters': 'scheduling/advanced-filters',
        'scheduling/get-partecipants-details': 'scheduling/get-partecipants-details/{reservationId}',
        'reservation-export': 'reservation-export',
        'jobs/orders': 'jobs/orders',
        'jobs/orders/id': 'jobs/orders/{id}',
        'jobs/products': 'jobs/products',
        'jobs/products/id': 'jobs/products/{id}',
        'jobs/products/id/error-report': 'jobs/products/{id}/error-report',
        'jobs/products/template': 'jobs/products/template',
        'base_template.xlsx': 'base_template.xlsx',
        'product_1_base_template.xlsx': 'product_1_base_template.xlsx',
        'api-order-register.pdf': 'api-order-register.pdf',
        'jobs/contacts': 'jobs/contacts',
        'jobs/contacts/id': 'jobs/contacts/{id}',
        'CRM_importing_contacts_company.xlsx': 'CRM_importing_contacts_company.xlsx',
        'CRM_importing_contacts_person.xlsx': 'CRM_importing_contacts_person.xlsx',
        'gifts': 'gifts',
        'gifts/id': 'gifts/{id}',
        'gifts/experiences-title': 'gifts/experiences-title',
        'gifts/resend': 'gifts/resend',
        'gifts/resend-emails': 'gifts/resend-emails',
        'gifts/redeem/giftId': 'gifts/redeem/{giftId}',
        'gifts/export': 'gifts/export',
        'gifts/create-voucher':'gifts/create-voucher',
        'gifts/byGiftUUID/giftId':'gifts/byGiftUUID/{giftId}',
        'v2/gifts/giftId/staff-note': 'v2/gifts/{giftId}/staff-note',
        'v2/gifts/giftId/redemption-preview': 'v2/gifts/{giftId}/redemption-preview',
        'v2/gifts/giftId/redeem': 'v2/gifts/{giftId}/redeem',
        'contacts/export': 'contacts/export',
        'contact-winery-presence': 'contact-winery-presence',
        'employees': 'employees',
        'employees/id': 'employees/{id}',
        'rooms': 'rooms',
        'rooms/id': 'rooms/{id}',
        'manual-reservations': 'scheduling/manual-reservations',
        'manual-reservations/id': 'scheduling/manual-reservations/{id}',
        'scheduling/reservation-form-options': 'scheduling/reservations/form-options',
        'scheduling/reservation-contacts/id': 'scheduling/reservation-contacts/{id}',
        'scheduling/reservation-contacts': 'scheduling/reservation-contacts',
        'experiences/short-description': 'experiences/short-description',
        'experiences/availabilities': 'experiences/availabilities',
        'experiences/availabilities/filter': 'experiences/availabilities/filter',
        'experiences/availabilities/resume': 'experiences/availabilities/resume',
        'admin/experiences/availabilities-with-fill': 'admin/experiences/availabilities-with-fill',
        'experiences/experienceId/availabilities': 'experiences/{experienceId}/availabilities',
        'experiences/experienceId/availabilities/date': 'experiences/{experienceId}/availabilities/{date}',
        'experiences/experienceId/availabilities/dayOfWeek-time': 'experiences/{experienceId}/availabilities/{dayOfWeek}-{time}',
        'experiences/experienceId/availabilities/override': 'experiences/{experienceId}/availabilities/override',
        //'experiences/experienceId/availabilities/override-count': 'experiences/{experienceId}/availabilities/override-count',
        "admin/experiences/experienceId/availabilities/override-count": "admin/experiences/{experienceId}/override-count",
        "experiences/experienceId/availabilities/date-time/exists": "experiences/{experienceId}/availabilities/{date}-{time}/exists",
        "experiences/experienceId/availability-summary/date-time": "experiences/{experienceId}/availability-summary/{date}-{time}",
        "experiences/export": "experiences/export",
        "experiences/import": "experiences/import",
        "experiences/active": "experiences/active",
        "experiences/sync": "experiences/sync",
        "availabilities/admin/sync/id": "availabilities/admin/sync/{id}",
        "availabilities/admin/full-sync/id": "availabilities/admin/full-sync/{id}",
        "experiences/experienceId/availabilities/full-sync": "experiences/{experienceId}/availabilities/full-sync",
        "experiences/id": "experiences/{id}",
        "experiences/list": "experiences/list",
        "v2/reservations/id": "v2/reservations/{id}",
        "v2/reservations/id/reject": "v2/reservations/{id}/reject",
        "v2/reservations/id/revoke": "v2/reservations/{id}/revoke",
        "v2/reservations/id/confirm": "v2/reservations/{id}/confirm",
        "v2/reservations/id/cancel": "v2/reservations/{id}/cancel",
        "v2/reservations/id/refund": "v2/reservations/{id}/refund",
        "experiences/event-availabilities": "experiences/event-availabilities",
        "experiences/experienceId/event-availabilities":
        "experiences/{experienceId}/event-availabilities",
        "experiences/experienceId/event-availabilities/day-time":
        "experiences/{experienceId}/event-availabilities/{day}-{time}",
        "experiences/experienceId/single-slot-availabilities":
        "experiences/{experienceId}/single-slot-availabilities",
        "experiences/experienceId/single-slot-availabilities/day-time":
        "experiences/{experienceId}/single-slot-availabilities/{day}-{time}",
        "experiences/experienceId/event-availabilities/override":
        "experiences/{experienceId}/event-availabilities/override",
        "admin/experiences/experienceId/event-availabilities/override-count":
        "admin/experiences/{experienceId}/override-count/single",
        "experiences/experienceId/single-slot-availabilities/override":
        "experiences/{experienceId}/single-slot-availabilities/override",
       'experiences/availabilities/move': 'experiences/availabilities/move',
        'experiences/availabilities/register': 'experiences/availabilities/register',
       'v2/closing-days/wineryId/filter': 'v2/closing-days/{wineryId}/filter',
        'v2/closing-days/wineryId': 'v2/closing-days/{wineryId}',
        'admin/experiences/experienceId/single-slot-availabilities/override-count': 'admin/experiences/{experienceId}/override-count/single',
        'experiences/experienceId/enabled-slot-automations': 'experiences/{experienceId}/enabled-slot-automations',
        'integrations': 'integrations',
        'integrations/integrationId': 'integrations/{integrationId}',
        'integrations/winelivery/winery/wineryId': 'integrations/winelivery/winery/{wineryId}',
        'integrations/winelivery/internal/experience/experienceId': 'integrations/winelivery/internal/experience/{experienceId}',
        'integrations/winelivery/internal/experience/experienceId/connect': 'integrations/winelivery/internal/experience/{experienceId}/connect',
        'integrations/winelivery/internal/experience/experienceId/disconnect': 'integrations/winelivery/internal/experience/{experienceId}/disconnect',
        'integrations/winelivery/experiences/wineryId': 'integrations/winelivery/experiences/{wineryId}',
        'integrations/experience/experienceId':'integrations/experience/{experienceId}',
        'integrations/winery/wineryId': 'integrations/winery/{wineryId}',
        'integrations/winery/wineryId/integrationCode': 'integrations/winery/{wineryId}/{integrationCode}',
        'integrations/for-experiences': 'integrations/for-experiences',
        'integrations/for-wineries': 'integrations/for-wineries',
        'integrations/active': 'integrations/active',
        'integrations/deactivate': 'integrations/deactivate',
        'integrations/create': 'integrations/create',
        'integrations/update': 'integrations/update',
        'integrations/delete': 'integrations/delete',
        'integrations/is-connected': 'integrations/is-connected',
        'integrations/integration/integrationId': 'integrations/integration/{integrationId}',
        'integrations/integration/integrationId/data-registry': 'integrations/integration/{integrationId}/data-registry',
        'integrations/integration/integrationId/options-registry': 'integrations/integration/{integrationId}/options-registry',
        'integrations/experience/experienceId/deactive': 'integrations/experience/{experienceId}/deactive',
        'integrations/experience/experienceId/active': 'integrations/experience/{experienceId}/active',
        'integrations/sync': 'integrations/sync',
        'integrations/integrationId/details': 'integrations/{integrationId}/details',
        'integrations/dwspay/create': 'integrations/dwspay/create',
        'integrations/dwspay/complete-onboarding': 'integrations/dwspay/complete-onboarding',
        'api-keys/active':'api-keys/active',
        'api-keys/deactivate':'api-keys/deactivate',
        'api-keys/delete':'api-keys/delete',
        'api-keys/create':'api-keys/create',
        'api-keys/update':'api-keys/update',
        'api-keys/generate-keys':'api-keys/generate-keys',
        'api-keys/apiKey':'api-keys/{apiKey}',
        'api-keys':'api-keys',
        'wswebhook/active':'wswebhook/active',
        'wswebhook/deactivate':'wswebhook/deactivate',
        'wswebhook/delete':'wswebhook/delete',
        'wswebhook/create':'wswebhook/create',
        'wswebhook/update':'wswebhook/update',
        'wswebhook/wsWebhookId':'wswebhook/{wsWebhookId}',
        'wswebhook':'wswebhook',
        'wswebhook/queue/webhookJobConfigurationId':'wswebhook/queue/{webhookJobConfigurationId}',
        'wswebhook/logs/webhookJobQueueId':'wswebhook/logs/{webhookJobQueueId}',
        'scheduling/integration-reservation/winelivery/id': 'scheduling/integration-reservation/winelivery/{id}',
        'cash-register/sync-reservation': 'cash-register/sync-reservation',
        'cash-register/sync-order': 'cash-register/sync-order',
        'cash-register/sync-preorder': 'cash-register/sync-preorder',
        'cash-register/sync-gift': 'cash-register/sync-gift',
        'cash-register/sync-product': 'cash-register/sync-product',
        'cash-register/sync-contact': 'cash-register/sync-contact',
        'cash-register/is-integrated': 'cash-register/is-integrated',
        'cash-register/get-metadata': 'cash-register/get-metadata',
        'cash-register/get-remote-status': 'cash-register/get-remote-status',
        'receipts/is-fiscality-integrated': 'receipts/is-fiscality-integrated',
        'receipts/list': 'receipts/list',
        'receipts/id': 'receipts/{id}',
        'receipts/id/pdf': 'receipts/{id}/pdf',
        'receipts/external/externalReceiptId': 'receipts/external/{externalReceiptId}',
        'receipts/sync': 'receipts/sync',
        'receipts/id/status': 'receipts/{id}/status',
        'receipts/id/receipt': 'receipts/{id}/receipt',
        'receipts/id/cancellation': 'receipts/{id}/cancellation',
        'receipts/id/correction': 'receipts/{id}/correction',
        'receipts/manual': 'receipts/manual',
        'receipts/send-email': 'receipts/send-email',
        'receipts/receiptId/audit-logs': 'receipts/{receiptId}/audit-logs',
        'receipts/receiptId/audit-logs/export': 'receipts/{receiptId}/audit-logs/export',
        'receipts/audit-logs/export': 'receipts/audit-logs/export',
        'fiskaly/wineryId/entities': 'fiskaly/{wineryId}/entities',
        'fiskaly/wineryId/entities/entityId': 'fiskaly/{wineryId}/entities/{entityId}',
        'fiskaly/wineryId/systems': 'fiskaly/{wineryId}/systems',
        'fiskaly/wineryId/systems/systemId': 'fiskaly/{wineryId}/systems/{systemId}',
        'fiskaly/wineryId/test-intention': 'fiskaly/{wineryId}/test-intention',
        'fiskaly/wineryId/test-transaction': 'fiskaly/{wineryId}/test-transaction',
        'fiskaly/wineryId/receipts/receiptId/retry-signit': 'fiskaly/{wineryId}/receipts/{receiptId}/retry-signit',
        'fiskaly/signit/account-status': 'fiskaly/signit/account-status',
        'fiskaly/signit/fisconline/update': 'fiskaly/signit/fisconline/update',
        'fiskaly/ops/signit/wineryId/onboarding/preview': 'fiskaly/ops/signit/{wineryId}/onboarding/preview',
        'fiskaly/ops/signit/wineryId/onboarding/apply': 'fiskaly/ops/signit/{wineryId}/onboarding/apply',
        'fiskaly/ops/signit/wineryId/onboarding/validate-json': 'fiskaly/ops/signit/{wineryId}/onboarding/validate-json',
        'fiskaly/ops/signit/wineryId/status': 'fiskaly/ops/signit/{wineryId}/status',
        'fiskaly/ops/signit/wineryId/taxpayer/fisconline-credentials': 'fiskaly/ops/signit/{wineryId}/taxpayer/fisconline-credentials',
        'fiskaly/ops/signit/wineryId/taxpayer/commission': 'fiskaly/ops/signit/{wineryId}/taxpayer/commission',
        'fiskaly/ops/signit/wineryId/locations/locationId/commission': 'fiskaly/ops/signit/{wineryId}/locations/{locationId}/commission',
        'fiskaly/ops/signit/wineryId/systems/systemId/commission': 'fiskaly/ops/signit/{wineryId}/systems/{systemId}/commission',
        'fiskaly/ops/signit/wineryId/taxpayers/taxpayerId': 'fiskaly/ops/signit/{wineryId}/taxpayers/{taxpayerId}',
        'fiskaly/ops/signit/wineryId/locations/locationId': 'fiskaly/ops/signit/{wineryId}/locations/{locationId}',
        'fiskaly/ops/signit/wineryId/systems/systemId': 'fiskaly/ops/signit/{wineryId}/systems/{systemId}',
        'fiskaly/ops/signit/wineryId/organizations/organizationId': 'fiskaly/ops/signit/{wineryId}/organizations/{organizationId}',
        'fiskaly/ops/signit/wineryId/subjects/subjectId': 'fiskaly/ops/signit/{wineryId}/subjects/{subjectId}',
        'preorders': 'preorders',
        'preorders/id': 'preorders/{id}',
        'preorders/id/accept': 'preorders/{id}/accept',
        'preorders/id/accept-with-id': 'preorders/{id}/accept-with-id',
        'preorders/id/reject': 'preorders/{id}/reject',
        'self-orders/by-winery': 'self-orders/{wineryId}',
        'self-orders/by-winery/export': 'self-orders/{wineryId}/export',
        'preorder/links': 'preorder/links',
        'account/authenticate': 'account/authenticate',
        'account/authenticate-mfa': 'account/authenticate-mfa',
        'account/authenticate-google': 'account/authenticate-google',
        'account/authenticate-microsoft': 'account/authenticate-microsoft',
        'account/select-login-context': 'account/select-login-context',
        'account/login-contexts': 'account/login-contexts',
        'account/super-admin/grant': 'account/super-admin/grant',
        'admin/winery/wineryId/impersonation-accounts': 'admin/winery/{wineryId}/impersonation-accounts',
        'admin/authorize': 'admin/authorize',
        'admin/authorize-as-winery': 'admin/authorize-as-winery',
        'self/account/edit': 'self/account/edit',
        'self/account/mfa/backup-codes/regenerate': 'self/account/mfa/backup-codes/regenerate',
        'self/account/mfa/disable': 'self/account/mfa/disable',
        'self/account/mfa/setup/confirm': 'self/account/mfa/setup/confirm',
        'self/account/mfa/setup/start': 'self/account/mfa/setup/start',
        'self/account/mfa/status': 'self/account/mfa/status',
        'self/account': 'self/account',
        'account': 'account',
        'account/reset-password/init': 'account/reset-password/init',
        'account/reset-password/check/{email}': 'account/reset-password/check/{email}',
        'account/reset-password/check-token/token': 'account/reset-password/check-token/{token}',
        'account/reset-password/finish': 'account/reset-password/finish',
        'account/onboarding/check/token': 'account/onboarding/check/{token}',
        'account/onboarding/finish': 'account/onboarding/finish',
        'account/mfa-reenroll/setup/confirm': 'account/mfa-reenroll/setup/confirm',
        'account/mfa-reenroll/setup/start': 'account/mfa-reenroll/setup/start',
        'account/mfa-reenroll/check/token': 'account/mfa-reenroll/check/{token}',
        'account/onboarding/mfa/setup/confirm': 'account/onboarding/mfa/setup/confirm',
        'account/onboarding/mfa/setup/start': 'account/onboarding/mfa/setup/start',
        'winery/wineryId/team-users': 'winery/{wineryId}/team-users',
        'winery/wineryId/team-users/accountId': 'winery/{wineryId}/team-users/{accountId}',
        'winery/wineryId/team-users/accountId/resend-onboarding-email': 'winery/{wineryId}/team-users/{accountId}/resend-onboarding-email',
        'winery/wineryId/team-users/accountId/reset-mfa': 'winery/{wineryId}/team-users/{accountId}/reset-mfa',
        'winery/wineryId/team-users/accountId/reset-password': 'winery/{wineryId}/team-users/{accountId}/reset-password',
        'winery/wineryId/team-users/accountId/deactivate': 'winery/{wineryId}/team-users/{accountId}/deactivate',
        'winery/wineryId/team-users/accountId/reactivate': 'winery/{wineryId}/team-users/{accountId}/reactivate',
        'winery/wineryId/team-users/roles-permissions': 'winery/{wineryId}/team-users/roles-permissions',
        'winery/wineryId/team-users/email-available': 'winery/{wineryId}/team-users/email-available',
        'winery/wineryId/user-limit': 'winery/{wineryId}/user-limit',
        'winery/wineryId': 'winery/{wineryId}',
        'winery/wineryId/homepage-data': 'winery/{wineryId}/homepage-data',
        'winery/wineryId/waitlist/subscribe': 'winery/{wineryId}/waitlist/subscribe',
        'winery/wineryId/waitlist/status': 'winery/{wineryId}/waitlist/status',
        'winery/wineryId/infowinery': 'winery/{wineryId}/infowinery',
        'winery/wineryId/images': 'winery/{wineryId}/images',
        'winery/wineryId/payment-method': 'winery/{wineryId}/payment-method',
        'winery/wineryId/experiences/payment/pay-at-winery/bulk-set': 'winery/{wineryId}/experiences/payment/pay-at-winery/bulk-set',
        'winery/wineryId/experiences/payment/pay-at-winery/bulk-unset': 'winery/{wineryId}/experiences/payment/pay-at-winery/bulk-unset',
        'winery/wineryId/experiences/payment/nexi/bulk-set': 'winery/{wineryId}/experiences/payment/nexi/bulk-set',
        'winery/wineryId/experiences/payment/nexi/bulk-unset': 'winery/{wineryId}/experiences/payment/nexi/bulk-unset',
        'winery/wineryId/experiences/payment/paypal/bulk-set': 'winery/{wineryId}/experiences/payment/paypal/bulk-set',
        'winery/wineryId/experiences/payment/paypal/bulk-unset': 'winery/{wineryId}/experiences/payment/paypal/bulk-unset',
        'winery/wineryId/experiences/payment/dwspay/bulk-set': 'winery/{wineryId}/experiences/payment/dwspay/bulk-set',
        'winery/wineryId/experiences/payment/dwspay/bulk-unset': 'winery/{wineryId}/experiences/payment/dwspay/bulk-unset',
        'winery/wineryId/wineryChannel': 'winery/{wineryId}/wineryChannel',
        'winery/wineryId/wineryChannel/id': 'winery/{wineryId}/wineryChannel/{id}',
        'winery/wineryId/attachment/type': 'winery/{wineryId}/attachment/{type}',
        'winery/wineryId/service': 'winery/{wineryId}/service',
        'winery/wineryId/service/id': 'winery/{wineryId}/service/{id}',
        'winery/wineryId/closingdays': 'winery/{wineryId}/closingdays',
        'winery/wineryId/closingdays/id': 'winery/{wineryId}/closingdays/{id}',
        'winery/wineryId/notification-email/add': 'winery/{wineryId}/notification-email/add',
        'winery/wineryId/notification-email/remove': 'winery/{wineryId}/notification-email/remove',
        'winery/wineryId/parameters': 'winery/{wineryId}/parameters',
        'winery/wineryId/parameters/key': 'winery/{wineryId}/parameters/{key}',
        'winery/wineryId/parameters/gift-duration': 'winery/{wineryId}/parameters/gift-duration',
        'winery/wineryId/parameters/active-billing-details': 'winery/{wineryId}/parameters/active-billing-details',
        'winery/wineryId/qr-codes': 'winery/{wineryId}/qr-codes',
        'winery/wineryId/qr-codes/reduced': 'winery/{wineryId}/qr-codes/reduced',
        'winery/wineryId/qr-codes/id': 'winery/{wineryId}/qr-codes/{id}',
        'winery/wineryId/qr-codes/id/enable': 'winery/{wineryId}/qr-codes/{id}/enable',
        'winery/wineryId/qr-codes/id/disable': 'winery/{wineryId}/qr-codes/{id}/disable',
        'winery/wineryId/calendar-configuration': 'winery/{wineryId}/calendar-configuration',
        'winery/wineryId/wineries': 'winery/{wineryId}/wineries',
        'wifi-providers/login': 'wifi-providers/login',
        'wifi-providers': 'wifi-providers',
        'wifi-providers/id': 'wifi-providers/{id}',
        'winery/wineryId/options/filters-widget': 'winery/{wineryId}/options/filters-widget',
        'ws-parameters': 'ws-parameters',
        'report-dashboard-reservation/filtered-reservations': 'report-dashboard-reservation/filtered-reservations',
        'report-dashboard-reservation/vital-stats': 'report-dashboard-reservation/vital-stats',
        'report-dashboard-reservation/time-stats': 'report-dashboard-reservation/time-stats',
        'report-dashboard-reservation/geo-stats': 'report-dashboard-reservation/geo-stats',
        'report-dashboard-reservation/origin-stats': 'report-dashboard-reservation/origin-stats',
        'report-dashboard-reservation/topexp-stats': 'report-dashboard-reservation/topexp-stats',
        'report-dashboard-reservation/language-stats': 'report-dashboard-reservation/language-stats',
        'report-dashboard-reservation/touroperator-stats': 'report-dashboard-reservation/touroperator-stats',
        'report-dashboard-reservation/bi-export': 'report-dashboard-reservation/bi-export',
        'report-dashboard-gifts/vital-stats': 'report-dashboard-gifts/vital-stats',
        'report-dashboard-gifts/time-stats': 'report-dashboard-gifts/time-stats',
        'report-dashboard-gifts/geo-stats': 'report-dashboard-gifts/geo-stats',
        'report-dashboard-gifts/origin-stats': 'report-dashboard-gifts/origin-stats',
        'report-dashboard-gifts/topexp-stats': 'report-dashboard-gifts/topexp-stats',
        'report-dashboard-gifts/bi-export': 'report-dashboard-gifts/bi-export',
        'report-dashboard-orders/vital-stats': 'report-dashboard-orders/vital-stats',
        'report-dashboard-orders/time-stats': 'report-dashboard-orders/time-stats',
        'report-dashboard-orders/geo-stats': 'report-dashboard-orders/geo-stats',
        'report-dashboard-orders/origin-stats': 'report-dashboard-orders/origin-stats',
        'report-dashboard-orders/topprod-stats': 'report-dashboard-orders/topprod-stats',
        'report-dashboard-orders/bi-export': 'report-dashboard-orders/bi-export',
        'report-dashboard-contacts/vital-stats': 'report-dashboard-contacts/vital-stats',
        'report-dashboard-contacts/time-stats': 'report-dashboard-contacts/time-stats',
        'report-dashboard-contacts/geo-stats': 'report-dashboard-contacts/geo-stats',
        'report-dashboard-contacts/origin-stats': 'report-dashboard-contacts/origin-stats',
        'report-dashboard-contact/growth-stats' : 'report-dashboard-contacts/growth-stats',
        'report-dashboard-contacts/toptags-stats': 'report-dashboard-contacts/toptags-stats',
        'report-dashboard-contacts/topsegments-stats': 'report-dashboard-contacts/topsegments-stats',
        'report-dashboard-contacts/topcustomers-stats': 'report-dashboard-contacts/topcustomers-stats',
        'report-dashboard-contacts/bi-export': 'report-dashboard-contacts/bi-export',
        'report-dashboard-marketing/vital-stats': 'report-dashboard-marketing/vital-stats',
        'report-dashboard-marketing/income-stats': 'report-dashboard-marketing/income-stats',
        'report-dashboard-marketing/time-stats': 'report-dashboard-marketing/time-stats',
        'report-dashboard-marketing/performance-time-stats': 'report-dashboard-marketing/performance-time-stats',
        'report-dashboard-marketing/conversion-stats': 'report-dashboard-marketing/conversion-stats',
        'report-dashboard-marketing/top-clicks-stats': 'report-dashboard-marketing/top-clicks-stats',
        'report-dashboard-marketing/top-opens-stats': 'report-dashboard-marketing/top-opens-stats',
        'report-dashboard-marketing/top-conversions-stats': 'report-dashboard-marketing/top-conversions-stats',
        'reservations/multiple': 'v2/reservations/multiple',
        'reservations/query': 'v2/reservations/query',
        'contact-emails/emailId': 'contact-emails/{id}',
        'contact-emails/resend/emailId': 'contact-emails/resend/{id}',
        'scheduling/reservation-history': 'scheduling/reservation-history',
        'forms': 'forms',
        'forms/id': 'forms/{formId}',
        'forms/id/activate': 'forms/{formId}/activate',
        'forms/id/deactivate': 'forms/{formId}/deactivate',
        'forms/id/clone': 'forms/{formId}/clone',
        'forms/id/draft': 'forms/{formId}/draft',
        'forms/id/answers': 'forms/{formId}/answers',
        'forms/id/answers/id': 'forms/{formId}/answers/{answerId}',
        'forms/id/urls': 'forms/{formId}/urls',
        'forms/id/notification-email': 'forms/{formId}/notification-email',
        'winery-origins': 'winery-origins',
        'winery-origins/urls': 'winery-origins/urls',
        'campaigns/id/archive': 'campaigns/{campaignId}/archive',
        'campaigns/id/unarchive': 'campaigns/{campaignId}/unarchive',
        'campaigns/id/retry': 'campaigns/{campaignId}/retry',
        'external-calendar/{id}/sync': 'external-calendar/{id}/sync',
        'wspayments/sales': 'wspayments/sales',
        'wspayments/sales/id/payment-links': 'wspayments/sales/{id}/payment-links',
        'wspayments/payment-links': 'wspayments/payment-links',
        'wspayments/payment-links/id': 'wspayments/payment-links/{id}',
        'wspayments/payment-links/id/send': 'wspayments/payment-links/{id}/send',
        'wspayments/accounts/payment-links/id/embedded-setup': 'wspayments/accounts/payment-links/{id}/embedded-setup',
        'wspayments/payment-links/id/regenerate': 'wspayments/payment-links/{id}/regenerate',
        'wspayments/payment-links/manual': 'wspayments/payment-links/manual',
        'wspayments/payments/id': 'wspayments/payments/{id}',
        'wspayments/payments': 'wspayments/payments',
        'wspayments/dwspay/winesuite/payments': 'wspayments/dwspay/winesuite/payments',
        'wspayments/dwspay/payments/query': 'wspayments/dwspay/payments/query',
        'wspayments/dwspay/payments/id': 'wspayments/dwspay/payments/{id}',
        'wspayments/dwspay/payouts/query': 'wspayments/dwspay/payouts/query',
        'wspayments/dwspay/payouts/id': 'wspayments/dwspay/payouts/{id}',
        'wspayments/dwspay/winesuite/payment-intent/id': 'wspayments/dwspay/winesuite/payment-intent/{id}',
        'wspayments/stripe-terminal/connection-token': 'wspayments/dwspay/terminal/connection-token',
        'wspayments/stripe-terminal/payment-intent': 'wspayments/dwspay/terminal/payment-intent',
        'wspayments/dwspay/winesuite-payment-intent/id': 'wspayments/dwspay/winesuite/payment-intent/{id}',
        'wspayments/stripe-terminal/payment-intent/capture': 'wspayments/dwspay/terminal/payment-intent/capture',
        'wspayments/stripe-terminal/setup-intent': 'wspayments/dwspay/terminal/setup-intent',
        'wspayments/stripe-terminal/setup-intent/id/confirm': 'wspayments/dwspay/terminal/setup-intent/{setupIntentId}/confirm',
        'wspayments/stripe-terminal/readers/collect-inputs': 'wspayments/dwspay/terminal/readers/collect-inputs',
        'wspayments/dwspay/terminal/terminals': 'wspayments/dwspay/terminal/terminals',
        'wspayments/dwspay/terminal/terminals/terminalId/reader': 'wspayments/dwspay/terminal/terminals/{terminalId}/reader',
        'wspayments/dwspay/terminal/locations': 'wspayments/dwspay/terminal/locations',
        'wspayments/dwspay/terminal/create-customer': 'wspayments/dwspay/terminal/create-customer',
        'wspayments/dwspay/terminal/create-customer/async': 'wspayments/dwspay/terminal/create-customer/async',
        'wspayments/dwspay/terminal/create-customer/status/jobId': 'wspayments/dwspay/terminal/create-customer/status/{jobId}',
        'wspayments/dwspay/terminal/create-customer/v2/async': 'wspayments/dwspay/terminal/create-customer/v2/async',
        'wspayments/dwspay/terminal/create-customer/v2/status/jobId': 'wspayments/dwspay/terminal/create-customer/v2/status/{jobId}',
        'wspayments/dwspay/terminal/create-customer/v3': 'wspayments/dwspay/terminal/create-customer/v3',
        'wspayments/dwspay/terminal/create-customer/v3/async': 'wspayments/dwspay/terminal/create-customer/v3/async',
        'wspayments/dwspay/terminal/create-customer/v3/status/jobId': 'wspayments/dwspay/terminal/create-customer/v3/status/{jobId}',
        'wspayments/dwspay/terminal/receipts/print':'wspayments/dwspay/terminal/receipts/print',
        'wspayments/dwspay/terminal/print-jobs': 'wspayments/dwspay/terminal/print-jobs',
        'wspayments/dwspay/terminal/terminals/terminalId/printers': 'wspayments/dwspay/terminal/terminals/{terminalId}/printers',
        'wspayments/dwspay/wspos/thermal-printers': 'wspayments/dwspay/wspos/thermal-printers',
        'wspayments/dwspay/wspos/thermal-printers/id': 'wspayments/dwspay/wspos/thermal-printers/{id}',
        'wspayments/dwspay/wspos/thermal-printers/id/rotate-credentials': 'wspayments/dwspay/wspos/thermal-printers/{id}/rotate-credentials',
        'wspayments/dwspay/wspos/thermal-printers/id/test-print': 'wspayments/dwspay/wspos/thermal-printers/{id}/test-print',
        'wspayments/dwspay/payment-intent': 'wspayments/dwspay/payment-intent',
        'wspayments/dwspay/payment-intent/id/metadata': 'wspayments/dwspay/payment-intent/{id}/metadata',
        'wspayments/dwspay/payment-intent/id/refund': 'wspayments/dwspay/payment-intent/{id}/refund',
        'wspayments/dwspay/connect/account-sessions': 'wspayments/dwspay/connect/account-sessions',
        'wspayments/stripe-connect/account-sessions': 'wspayments/stripe-connect/account-sessions',
        'wspayments/accounts/winery/id/dwspay': 'wspayments/accounts/winery/{id}/dwspay',
        'wspayments/accounts/dwspay/create': 'wspayments/accounts/dwspay/create',
        'wspayments/billing/account/subscriptions': 'wspayments/billing/account/subscriptions',
        'wspayments/billing/account/charges': 'wspayments/billing/account/charges',
        'wspayments/billing/account/application-fees': 'wspayments/billing/account/application-fees',
        'wspayments/billing/account/application-fees/range': 'wspayments/billing/account/application-fees/range',
        'wspayments/billing/account/accounts': 'wspayments/billing/account/accounts',
        'wspayments/billing/account/customer': 'wspayments/billing/account/customer',
        'wspayments/dwspay/billing/report/advanced-export': 'wspayments/dwspay/billing/report/advanced-export',
        'admin/billing/sync-all-legacy': 'admin/billing/sync-all-legacy',
        'admin/billing/info': 'admin/billing/info',
        'admin/billing/create-divinea-invoices': 'admin/billing/create-divinea-invoices',
        'admin/billing/create-wspayments-invoices': 'admin/billing/create-wspayments-invoices',
        'admin/billing/create-divinea-invoices-async': 'admin/billing/create-divinea-invoices-async',
        'admin/billing/create-wspayments-invoices-async': 'admin/billing/create-wspayments-invoices-async',
        'admin/billing/issued-documents-not-sent': 'admin/billing/issued-documents-not-sent',
        'admin/billing/issued-documents-not-sent-async': 'admin/billing/issued-documents-not-sent-async',
        'admin/billing/issued-documents-in-range': 'admin/billing/issued-documents-in-range',
        'admin/billing/issued-documents-in-range-async': 'admin/billing/issued-documents-in-range-async',
        'admin/billing/issued-documents/{documentId}/date': 'admin/billing/issued-documents/{documentId}/date',
        'admin/billing/send-einvoices-bulk': 'admin/billing/send-einvoices-bulk',
        'admin/billing/send-einvoices-bulk-async': 'admin/billing/send-einvoices-bulk-async',
        'admin/billing/jobs/{jobId}': 'admin/billing/jobs/{jobId}',
        'admin/billing/fees/search-async': 'admin/billing/fees/search-async',
        'admin/billing/fees/search-async/{jobId}': 'admin/billing/fees/search-async/{jobId}',
        'admin/billing/lookup-stripe-candidates': 'admin/billing/lookup-stripe-candidates',
        'admin/billing/verify-stripe-alignment': 'admin/billing/verify-stripe-alignment',
        'admin/billing/export-cf-alignment': 'admin/billing/export-cf-alignment',
        'admin/billing/save-billing-info-batch': 'admin/billing/save-billing-info-batch',
        'wspayments/subscriptions': 'wspayments/subscriptions',
        'wspayments/subscriptions/id': 'wspayments/subscriptions/{id}',
        'wspayments/subscriptions/id/cancel': 'wspayments/subscriptions/{id}/cancel',
        'wspayments/subscriptions/from-terminal-payment': 'wspayments/subscriptions/from-terminal-payment',
        'ping': 'ping',
        'experiences/generate/details': 'experiences/generate/details',
        'experiences/generate/image': 'experiences/generate/image',
        'experiences/generate/translate/details': 'experiences/generate/translate/details',
        'experiences/translate/details': 'experiences/translate/details',
        'v2/coupons': 'v2/coupons',
        'v2/coupons/list': 'v2/coupons/list',
        'v2/coupons/id': 'v2/coupons/{id}',
        'v2/customerclass': 'v2/customerclass',
        'notification/send': 'notification/send',
        'notification/notifications/list': 'notification/notifications/list',
        'notification/with-id/id': 'notification/with-id/{id}',
        'notification/messages/list': 'notification/messages/list',
        'notification/messages/interaction-log': 'notification/messages/interaction-log',
        'notification/read': 'notification/read',
        'notification/read/all-messages': 'notification/read/all-messages',
        'notification/read/all-notifications': 'notification/read/all-notifications',
        'notification-tokens/create-renew':'notification-tokens/create-renew',
        "resend-reservation-email/email-logs/salesId": "resend-reservation-email/email-logs/{salesId}",
        "resend-reservation-email/resend-scheduled": "resend-reservation-email/resend-scheduled",
        "resend-reservation-email/resend-confirmed": "resend-reservation-email/resend-confirmed",
        "resend-reservation-email/resend-completed": "resend-reservation-email/resend-completed",
        "resend-reservation-email/resend-rescheduled": "resend-reservation-email/resend-rescheduled",
        "resend-reservation-email/resend-revoked": "resend-reservation-email/resend-revoked",
        "resend-reservation-email/resend-rejected": "resend-reservation-email/resend-rejected",
        "resend-reservation-email/emails/list": "resend-reservation-email/emails/list",
        "resend-reservation-email/gifts/resend-emails": "gifts/resend-emails",
        "v2/contacts/duplicates-by-email": "v2/contacts/duplicates-by-email",
        "v2/contacts/merge/contactIdMaster": "v2/contacts/merge/{contactIdMaster}",
        "v2/contacts/merge-all": "v2/contacts/merge-all",
        'public/reservations/check-in-qr-code': 'public/reservations/check-in-qr-code',
        'public/reservations/get-info-reservation': 'public/reservations/get-info-reservation',
        'forecasts/reservations': 'forecasts/reservations',
        'forecasts/orders': 'forecasts/orders',
        'v2/experiences': 'v2/experiences',
        'v2/experiences/id': 'v2/experiences/{id}',
        'v3/experiences': 'v3/experiences',
        'v3/experiences/id': 'v3/experiences/{id}',
        'v3/experiences/query': 'v3/experiences/query',
        'v2/experiences/preview-propagation': 'v2/experiences/preview-propagation',
        'v2/experiences/sync/id': 'v2/experiences/sync/{id}',
        'v2/experiences/priority': 'v2/experiences/priority',
        'v2/additional-questions': 'v2/additional-questions',
        'v2/additional-questions/id': 'v2/additional-questions/{id}',
        'v2/additional-questions/id/has-answers': 'v2/additional-questions/{id}/has-answers',
        'v2/experiences/experienceId/additional-questions': 'v2/experiences/{experienceId}/additional-questions',
        'v2/reservations/reservationId/additional-question-answers': 'v2/reservations/{reservationId}/additional-question-answers',
        'v2/reservations/additional-question-answers/export': 'v2/reservations/additional-question-answers/export',
        'v2/winesuite-jobs-async/jobId': 'v2/winesuite-jobs-async/{jobId}',
        'v2/winesuite-jobs-async/jobId/report-csv': 'v2/winesuite-jobs-async/{jobId}/report.csv',
        'v2/winesuite-jobs-async/jobId/report-xlsx': 'v2/winesuite-jobs-async/{jobId}/report.xlsx',
        'v2/admin/winesuite-jobs-async/jobId/restart': 'v2/admin/winesuite-jobs-async/{jobId}/restart',
        "v2/availabilities/actions": "v2/availabilities/actions",
        "experiences/availability-with-fill": "experiences/availability-with-fill",
        "terms-and-conditions": "terms-and-conditions",
        "terms-and-conditions/to-accept": "terms-and-conditions/to-accept",
        "terms-and-conditions/latest-ws-payment-acceptance": "terms-and-conditions/latest-ws-payment-acceptance",
        "account/check-password": "account/check-password",
        "cloudinary/upload/file": "cloudinary/upload/file",
        "cloudinary/upload/base64": "cloudinary/upload/base64",
        "cloudinary/upload/temp": "cloudinary/upload/temp",
        "cloudinary/config": "cloudinary/config",
        "cloudinary/promote-to-store": "cloudinary/promote-to-store",
        "cloudinary/upload-signature": "cloudinary/upload-signature",
        "cloudinary/move": "cloudinary/move",
        'ping-from-ws': 'ping-from-ws',
        'email-messages': 'email-messages',
        'email-messages/id/cancel': 'email-messages/{id}/cancel',
        'email-messages/id/reactivate': 'email-messages/{id}/reactivate',
      }
    },
    v2: {
      root: "/api/v2/",
      endpoints: {
        tokens: "tokens",
        "notification-tokens": "notification-tokens",
        password: "password",
        host: "host",
        "host/reservations": "host/reservations",
        "host/company": "host/company",
        "host/company/self": "host/company/self",
        "host/company/reduced": "host/company/reduced",
        "host/company/contract": "host/company/contract",
        "host/company/dws-pro": "host/company/dws-pro",
        "host/company/stripe-connect-link": "host/company/stripe-connect-link",
        "host/company/paypal-partner-referral-link": "host/company/paypal-partner-referral-link",
        "host/reservations/id": "host/reservations/{id}",
        "host/gifts/id/reservations": "host/gifts/{id}/reservations",
        "host/reservations/export-csv": "host/reservations/export-csv",
        "host/appointments": "host/appointments",
        "host/appointments/id": "host/appointments/{id}",
        "host/experiences": "host/experiences",
        "host/experiences/id": "host/experiences/{id}",
        "host/experiences/id/activate": "host/experiences/{id}/activate",
        "host/experiences/id/deactivate": "host/experiences/{id}/deactivate",
        "host/experiences/id/availability-override": "host/experiences/{id}/availability-override",
        "host/experiences-name": "host/experiences-name",
        "host/clients": "host/clients",
        "host/products": "host/products",
        "host/google-authorization": "host/google-authorization",
        "host/products/id": "host/products/{id}",
        "host/push-notifications": "host/push-notifications",
        "host/push-notifications/mex": "host/push-notifications/mex",
        "host/push-notifications/mark-as-viewed": "host/push-notifications/mark-as-viewed",
        "host/push-notifications/mark-as-viewed-single": "host/push-notifications/mark-as-viewed-single",
        "host/push-notifications/id": "host/push-notifications/{id}",
        "host/emails/id": "host/emails/{id}",
        "host/emails/next-scheduled": "host/emails/next-scheduled",
        "host/emails/id/resend": "host/emails/{id}/resend",
        "host/coupons": "host/coupons",
        "host/coupons/id": "host/coupons/{id}",
        "host/rooms": "host/rooms",
        "host/employees": "host/employees",
        "host/reservation-form-options": "host/reservation-form-options",
        "host/reservations/detail/id": "host/reservations/detail/{id}",
        "host/connect-paypal": "host/connect-paypal",
        "host/connect-nexi": "host/connect-nexi",
        "host/disconnect-payment": "host/disconnect-payment",
        "host/products-quantity-activated": "host/products-quantity-activated",
        "host/products/id/change-priority": "host/products/{id}/change-priority",
        "host/experiences-quantity-activated": "host/experiences-quantity-activated",
        "host/experiences/id/change-priority": "host/experiences/{id}/change-priority",
        "experiences/id/availabilities": "experiences/{id}/availabilities",
        "experiences/id": "experiences/{id}",
        experiences: "experiences",
        registrations: "registrations",
        coupons: "coupons",
        "host/coupons-valid": "host/coupons-valid",
        "companies/id": "companies?ids={id}",
        "manual-origins": "manual-origins",
        "manual-payment-methods": "manual-payment-methods",
        "payment-page": "payment-page",
      },
    },
    v1: {
      root: "/api/v1",
      endpoints: {
        host: "host",
        "host/reservations": "host/reservations",
        "host/reservations/id/confirm": "host/reservations/{id}/confirm",
        "host/reservations/id/reject": "host/reservations/{id}/reject",
        reservations: "reservations",
        "tag-categories": "tag_categories",
        "gifts/id/update": "gifts/{giftId}/update",
        "gifts/id/send_manual_email": "gifts/{giftId}/send_manual_email",
      },
    },
    divineaCom: {
      root: "/",
      endpoints: {
        "countries/v2/all": "countries/v2/all",
      },
    },
  },
};